/* Glamojo: variant gallery with cache and idle prefetch */

(() => {
  const controllers = new Map();
  const galleryCache = new Map();
  const prefetching = new Set();

  const getCacheKey = (sectionId, variantId) =>
    `${sectionId}:${variantId}`;

  const getWrapper = (productMedia) =>
    productMedia?.closest(
      '.gsp-main-product-media-wrapper'
    );

  const updateAvailability = (
    section,
    variantId
  ) => {
    const availability =
      section.querySelector(
        '[data-glamojo-inventory]'
      );

    if (!availability || !variantId) {
      return;
    }

    let inventory = {};

    try {
      inventory = JSON.parse(
        availability.dataset.glamojoInventory
      );
    } catch {
      return;
    }

    const variant =
      inventory[String(variantId)];

    if (!variant) return;

    if (!variant.available) {
      availability.textContent =
        'Unavailable';

      return;
    }

    if (
      variant.tracked &&
      variant.quantity > 0 &&
      variant.quantity <= 5
    ) {
      availability.textContent =
        `Only ${variant.quantity} left · Ready to ship`;

      return;
    }

    if (
      variant.tracked &&
      variant.quantity >= 6 &&
      variant.quantity <= 10
    ) {
      availability.textContent =
        'Low stock · Ready to ship';

      return;
    }

    availability.textContent =
      'In stock · Ready to ship';
  };

  const showGallery = (productMedia) => {
    if (!productMedia) return;

    const wrapper = getWrapper(productMedia);

    wrapper?.classList.remove(
      'glamojo-gallery-loading'
    );

    wrapper?.removeAttribute('aria-busy');

    [
      '--glamojo-gallery-overlay-top',
      '--glamojo-gallery-overlay-left',
      '--glamojo-gallery-overlay-width',
      '--glamojo-gallery-overlay-height'
    ].forEach((property) => {
      wrapper?.style.removeProperty(property);
    });

    productMedia.style.visibility = '';
    productMedia.style.minHeight = '';
    productMedia.style.pointerEvents = '';
  };

  const hideGallery = (productMedia) => {
    if (!productMedia) return;

    const wrapper = getWrapper(productMedia);
    const featured = productMedia.querySelector(
      '.product-featured'
    );

    if (wrapper && featured) {
      const wrapperRect =
        wrapper.getBoundingClientRect();

      const featuredRect =
        featured.getBoundingClientRect();

      wrapper.style.setProperty(
        '--glamojo-gallery-overlay-top',
        `${featuredRect.top - wrapperRect.top}px`
      );

      wrapper.style.setProperty(
        '--glamojo-gallery-overlay-left',
        `${featuredRect.left - wrapperRect.left}px`
      );

      wrapper.style.setProperty(
        '--glamojo-gallery-overlay-width',
        `${featuredRect.width}px`
      );

      wrapper.style.setProperty(
        '--glamojo-gallery-overlay-height',
        `${featuredRect.height}px`
      );

      wrapper.classList.add(
        'glamojo-gallery-loading'
      );

      wrapper.setAttribute('aria-busy', 'true');
    }

    productMedia.style.minHeight =
      `${productMedia.offsetHeight}px`;
  };

  const preloadImage = (
    sourceImage,
    fetchPriority = 'low'
  ) => {
    if (!sourceImage) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const image = new Image();
      let completed = false;

      const finish = () => {
        if (completed) return;

        completed = true;
        resolve();
      };

      image.onload = finish;
      image.onerror = finish;

      if (sourceImage.sizes) {
        image.sizes = sourceImage.sizes;
      }

      if (sourceImage.srcset) {
        image.srcset = sourceImage.srcset;
      }

      image.fetchPriority = fetchPriority;
      image.src =
        sourceImage.currentSrc ||
        sourceImage.src;

      if (image.complete) {
        finish();
      }

      window.setTimeout(finish, 1500);
    });
  };

  const preloadGalleryImages = (
    productMedia,
    fetchPriority = 'high'
  ) => {
    const images = Array.from(
      productMedia?.querySelectorAll(
        '#product-slider-featured img, #product-slider-thumb img'
      ) || []
    );

    if (!images.length) {
      return Promise.resolve();
    }

    const firstImagePromise = preloadImage(
      images[0],
      fetchPriority
    );

    images.slice(1).forEach((image) => {
      preloadImage(image, 'low');
    });

    return firstImagePromise;
  };

  const getGalleryHtml = async (
    productMedia,
    sectionId,
    variantId,
    signal
  ) => {
    const cacheKey = getCacheKey(
      sectionId,
      variantId
    );

    const cachedHtml =
      galleryCache.get(cacheKey);

    if (cachedHtml) {
      return cachedHtml;
    }

    const url = new URL(
      productMedia.dataset.productUrl,
      window.location.origin
    );

    url.searchParams.set(
      'variant',
      variantId
    );

    url.searchParams.set(
      'section_id',
      sectionId
    );

    const response = await fetch(url, {
      signal,
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error(
        `Section request failed: ${response.status}`
      );
    }

    const html = await response.text();

    const parsedResponse =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const responseMedia =
      parsedResponse.querySelector(
        'product-media[data-product-id]'
      );

    if (!responseMedia) {
      throw new Error(
        'Product gallery not found'
      );
    }

    const galleryHtml =
      responseMedia.outerHTML;

    galleryCache.set(
      cacheKey,
      galleryHtml
    );

    return galleryHtml;
  };

  const prefetchVariantGallery = async (
    productMedia,
    sectionId,
    variantId
  ) => {
    const cacheKey = getCacheKey(
      sectionId,
      variantId
    );

    if (
      galleryCache.has(cacheKey) ||
      prefetching.has(cacheKey)
    ) {
      return;
    }

    prefetching.add(cacheKey);

    try {
      const galleryHtml =
        await getGalleryHtml(
          productMedia,
          sectionId,
          variantId
        );

      const parsedGallery =
        new DOMParser().parseFromString(
          galleryHtml,
          'text/html'
        );

      const gallery =
        parsedGallery.querySelector(
          'product-media[data-product-id]'
        );

      if (gallery) {
        preloadGalleryImages(
          gallery,
          'low'
        );
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn(
          'Glamojo prefetch:',
          error
        );
      }
    } finally {
      prefetching.delete(cacheKey);
    }
  };

  const prefetchAllVariantGalleries = (
    productMedia,
    sectionId
  ) => {
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (
      connection?.saveData ||
      ['slow-2g', '2g'].includes(
        connection?.effectiveType
      )
    ) {
      return;
    }

    let variantIds = [];

    try {
      variantIds = JSON.parse(
        productMedia.dataset
          .glamojoVariantIds || '[]'
      );
    } catch {
      return;
    }

    const uniqueVariantIds = [
      ...new Set(
        variantIds
          .map(Number)
          .filter(Boolean)
      )
    ];

    if (!uniqueVariantIds.length) {
      return;
    }

    const startPrefetch = async () => {
      const queue = [...uniqueVariantIds];
      const workers = [];

      const worker = async () => {
        while (queue.length) {
          const variantId = queue.shift();

          await prefetchVariantGallery(
            productMedia,
            sectionId,
            variantId
          );
        }
      };

      for (
        let index = 0;
        index < 2;
        index += 1
      ) {
        workers.push(worker());
      }

      await Promise.all(workers);
    };

    if (
      'requestIdleCallback' in window
    ) {
      window.requestIdleCallback(
        startPrefetch,
        { timeout: 3000 }
      );
    } else {
      window.setTimeout(
        startPrefetch,
        2000
      );
    }
  };

  const refreshGallery = async (
    productMedia,
    variantId
  ) => {
    if (!productMedia || !variantId) {
      return;
    }

    const section = productMedia.closest(
      '[data-section-id]'
    );

    const sectionId =
      section?.dataset.sectionId;

    if (!section || !sectionId) {
      showGallery(productMedia);
      return;
    }

    controllers.get(sectionId)?.abort();

    const controller =
      new AbortController();

    controllers.set(
      sectionId,
      controller
    );

    try {
      const galleryHtml =
        await getGalleryHtml(
          productMedia,
          sectionId,
          variantId,
          controller.signal
        );

      if (
        controllers.get(sectionId) !==
        controller
      ) {
        return;
      }

      const parsedGallery =
        new DOMParser().parseFromString(
          galleryHtml,
          'text/html'
        );

      const updatedMedia =
        parsedGallery.querySelector(
          'product-media[data-product-id]'
        );

      const currentMedia =
        section.querySelector(
          'product-media[data-product-id]'
        );

      if (!updatedMedia || !currentMedia) {
        showGallery(currentMedia);
        return;
      }

      await preloadGalleryImages(
        updatedMedia,
        'high'
      );

      if (
        controllers.get(sectionId) !==
        controller
      ) {
        return;
      }

      const currentHeight =
        currentMedia.offsetHeight;

      updatedMedia.style.visibility =
        'hidden';

      updatedMedia.style.pointerEvents =
        'none';

      updatedMedia.style.minHeight =
        `${currentHeight}px`;

      currentMedia.replaceWith(
        updatedMedia
      );

      window.requestAnimationFrame(() => {
        window.GSP?.slickSlider?.init?.();

        window.requestAnimationFrame(() => {
          showGallery(updatedMedia);
        });
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      showGallery(
        section.querySelector(
          'product-media[data-product-id]'
        )
      );

      console.error(
        'Glamojo variant gallery:',
        error
      );
    } finally {
      if (
        controllers.get(sectionId) ===
        controller
      ) {
        controllers.delete(sectionId);
      }
    }
  };

  const init = () => {
    document
      .querySelectorAll('[data-section-id]')
      .forEach((section) => {
        const productMedia =
          section.querySelector(
            'product-media[data-product-id]'
          );

        const variantPicker =
          section.querySelector(
            'variant-picker'
          );

        const sectionId =
          section.dataset.sectionId;

        if (
          !productMedia ||
          !variantPicker ||
          !sectionId
        ) {
          return;
        }

        if (
          section.dataset
            .glamojoVariantGalleryReady ===
          'true'
        ) {
          return;
        }

        section.dataset
          .glamojoVariantGalleryReady = 'true';

        prefetchAllVariantGalleries(
          productMedia,
          sectionId
        );

        section.addEventListener(
          'change',
          (event) => {
            if (
              !event.target.closest(
                'variant-picker'
              )
            ) {
              return;
            }

            const currentMedia =
              section.querySelector(
                'product-media[data-product-id]'
              );

            hideGallery(currentMedia);

            window.setTimeout(() => {
              const variantInput =
                section.querySelector(
                  'form[id^="product-form-"] [name="id"]'
                ) ||
                section.querySelector(
                  'input[name="id"]'
                );

              const latestMedia =
                section.querySelector(
                  'product-media[data-product-id]'
                );

              const variantId = Number(
                variantInput?.value
              );

              updateAvailability(
                section,
                variantId
              );

              if (
                !latestMedia ||
                !variantId
              ) {
                showGallery(latestMedia);
                return;
              }

              refreshGallery(
                latestMedia,
                variantId
              );
            }, 0);
          },
          true
        );
      });
  };

  const scheduleInit = () => {
    window.setTimeout(init, 0);
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      scheduleInit
    );
  } else {
    scheduleInit();
  }

  document.addEventListener(
    'shopify:section:load',
    scheduleInit
  );
})();